import React, { useEffect, useMemo } from "react";
import { TransportFormData } from "./transportTypes";
import { Trip, TransportData } from "@/types";
import { motion } from "framer-motion";
import { DateTime } from "luxon";

interface TransportPreviewProps {
  formData: TransportFormData;
  departureTime: string;
  arrivalTime: string;
  transportData: TransportData;
  tripData: Trip;
}

const TransportPreview: React.FC<TransportPreviewProps> = ({
  formData,
  departureTime,
  arrivalTime,
  transportData,
  tripData,
}) => {
  // Get first leg departure time and last leg arrival time
  const firstLeg = formData.legs[0] || {
    departureTime: "",
    arrivalTime: "",
    provider: "",
    providerName: "",
    providerImage: "",
    cost: 0,
    originCode: "",
    destinationCode: "",
  };
  const lastLeg = formData.legs[formData.legs.length - 1] || firstLeg;

  const calculateDuration = (
    departureTime: string,
    arrivalTime: string,
    timezone1: string,
    timezone2: string
  ) => {
    const departure = DateTime.fromISO(departureTime, {
      zone: timezone1,
    });
    const arrival = DateTime.fromISO(arrivalTime, {
      zone: timezone2,
    });

    // Convert both to UTC before calculating duration
    const durationMilliseconds = arrival
      .toUTC()
      .diff(departure.toUTC())
      .as("milliseconds");
    const durationMinutes = Math.round(durationMilliseconds / (1000 * 60));
    return durationMinutes;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Calculate total cost - either from the individual legs or the total input
  const calculateTotalCost = () => {
    const legCostsExist = formData.legs.some(
      (leg) => leg.cost !== undefined && leg.cost !== null
    );
    if (legCostsExist) {
      return formData.legs.reduce(
        (sum, leg) => sum + (Number(leg.cost) || 0),
        0
      );
    }
    return 0;
  };

  const totalCost = calculateTotalCost();

  // Determine if we have multiple providers
  const providers = new Set(
    formData.legs.map((leg) => leg.provider).filter(Boolean)
  );

  // Get unique providers with images for rendering
  const uniqueProviders = formData.legs
    .filter(
      (leg, index, self) =>
        leg.provider &&
        self.findIndex((l) => l.provider === leg.provider) === index
    )
    .map((leg) => ({
      name: leg.providerName || leg.provider,
      code: leg.provider,
      logo: leg.providerImage,
    }));

  const providerDisplay =
    providers.size > 1
      ? "Multiple Airlines"
      : firstLeg.providerName || firstLeg.provider || "Select Provider";

  // Format time function
  const formatTime = (dateString: string) => {
    if (!dateString) return "--:--";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Memoize the transport SVG to prevent unnecessary re-renders
  const TransportSVG = useMemo(
    () => (
      <svg
        className="w-4 h-4 text-blue-600"
        stroke="currentColor"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 510 510"
      >
        <path d="M510 255c0-20.4-17.85-38.25-38.25-38.25H331.5L204 12.75h-51l63.75 204H76.5l-38.25-51H0L25.5 255 0 344.25h38.25l38.25-51h140.25l-63.75 204h51l127.5-204h140.25c20.4 0 38.25-17.85 38.25-38.25z" />
      </svg>
    ),
    []
  );

  // Cleanup SVG elements on unmount
  useEffect(() => {
    return () => {
      const svgElements = document.querySelectorAll("svg");
      svgElements.forEach((svg) => {
        const gradients = svg.querySelectorAll("linearGradient");
        gradients.forEach((gradient) => gradient.remove());
      });
    };
  }, []);

  return (
    <div className="flex flex-row h-full">
      {/* MAIN */}
      <div className="w-[75%] flex flex-col">
        {/* Header Section */}
        <div className="w-full flex flex-row items-center">
          <div className="flex flex-col justify-center pt-1 pb-1 pl-3 pr-3 bg-neutral-100 rounded-lg">
            <h1 className="text-base text-neutral-700 font-semibold text-center">
              {departureTime
                ? DateTime.fromISO(departureTime)
                    .setZone(transportData.locationAbove?.date.time_zone)
                    .toFormat("dd")
                : "--:--"}
            </h1>
            <h1 className="text-xs text-neutral-500 text-center">
              {/* Enter month here */}
              {departureTime
                ? DateTime.fromISO(departureTime)
                    .setZone(transportData.locationAbove?.date.time_zone)
                    .toFormat("MMMM")
                : "--:--"}
            </h1>
          </div>
          <div className="flex flex-col ml-2">
            <h1 className="text-[17px] text-neutral-900 font-semibold">
              {providerDisplay}
            </h1>
            <div className="flex flex-row items-center justify-start mt-0.5">
              <h1 className="text-xs font-medium text-neutral-500">
                {providers.size > 1
                  ? `${providers.size} airlines`
                  : firstLeg.providerName || firstLeg.provider || "---"}
              </h1>
              <div className="w-1 h-1 bg-neutral-300 rounded-full ml-2 mr-2"></div>
              <p className="text-neutral-500 font-light text-xs text-center">
                {departureTime && arrivalTime
                  ? formatDuration(
                      calculateDuration(
                        departureTime,
                        arrivalTime,
                        transportData.locationAbove?.date.time_zone || "",
                        transportData.locationBelow?.date.time_zone || ""
                      )
                    )
                  : "--:--"}
              </p>
            </div>
          </div>
          <div className="ml-auto mr-2 flex">
            {uniqueProviders.map((provider, index) => (
              <div
                key={provider.code}
                className="airline-avatar rounded-full border-2 border-white shadow-sm bg-white"
                style={{
                  width: "2.5rem",
                  height: "2.5rem",
                  backgroundImage: provider.logo
                    ? `url(${provider.logo})`
                    : undefined,
                  backgroundColor: !provider.logo ? "#f3f4f6" : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  marginLeft: index > 0 ? "-1rem" : "0",
                  zIndex: uniqueProviders.length - index,
                }}
              />
            ))}
          </div>
        </div>

        <div className="w-full bg-neutral-300 mt-3 h-[1px]"></div>

        {/* Journey Section */}
        <div className="w-full flex flex-row justify-between px-2.5 items-center mt-4 mb-1">
          {/* Departure Time */}
          <div className="flex-none flex flex-col justify-center items-center">
            <p className="text-[17px] text-neutral-800 font-semibold text-center">
              {departureTime
                ? DateTime.fromISO(departureTime)
                    .setZone(transportData.locationAbove?.date.time_zone)
                    .toFormat("HH:mm")
                : "--:--"}
            </p>
            <p className="text-xs text-neutral-400 text-center">
              {transportData.locationAbove?.name || "Departure"}
            </p>
          </div>

          {/* Journey Visualization */}
          <div className="w-1/2 flex flex-col justify-center mt-2">
            {!formData.numberOfLegs ||
            formData.numberOfLegs === "" ||
            Number(formData.numberOfLegs) === 1 ? (
              <div className="h-[52px] flex flex-col">
                <div className="h-[24px] flex flex-row space-x-3 items-center justify-center">
                  <h2 className="px-2 rounded-full py-0.5 bg-neutral-100 text-[11px] text-neutral-600 font-semibold">
                    Direct
                  </h2>
                </div>
                <div className="h-[28px] flex flex-row items-center">
                  <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                  <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                  <div className="flex-none flex items-center justify-center bg-white rounded-full mx-1 w-4 h-4">
                    {TransportSVG}
                  </div>
                  <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                  <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                </div>
              </div>
            ) : (
              <div className="h-[52px] flex flex-col">
                <div className="h-[24px] flex flex-row space-x-4 items-center justify-center">
                  <h2 className="px-2 py-0.5 rounded-full bg-orange-100 text-[10px] text-orange-600 font-semibold">
                    {Number(formData.numberOfLegs) - 1} stop
                    {Number(formData.numberOfLegs) - 1 > 1 ? "s" : ""}
                  </h2>
                </div>
                <div className="h-[28px] flex flex-row items-center">
                  <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                  <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                  <div className="flex items-center justify-center w-4 h-4">
                    <div className="h-3 w-3 bg-orange-600 rounded-full"></div>
                  </div>
                  <div className="h-[2px] bg-neutral-300 flex-grow"></div>
                  <div className="bg-blue-600 rounded-full w-2 h-2"></div>
                </div>
              </div>
            )}
          </div>

          {/* Arrival Time */}
          <div className="flex-none flex flex-col items-center">
            <p className="text-[17px] text-neutral-800 font-semibold text-center">
              {arrivalTime
                ? DateTime.fromISO(arrivalTime)
                    .setZone(transportData.locationBelow?.date.time_zone)
                    .toFormat("HH:mm")
                : "--:--"}
            </p>
            <p className="text-xs text-neutral-400 text-center">
              {transportData.locationBelow?.name || "Arrival"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-neutral-300 w-[1px] mx-2"></div>

      {/* Price Section */}
      <div className="w-[20%] flex flex-col justify-center items-center">
        <h2 className="text-xs text-neutral-400">Total cost</h2>
        <span className="text-lg text-neutral-800 mt-1 font-semibold">
          ${totalCost.toFixed(2)}
        </span>
        <h1 className="text-xs text-neutral-400">per person</h1>
      </div>
    </div>
  );
};

export default TransportPreview;
